import React, { useState, useRef } from 'react';
import { X, Images } from 'lucide-react';
import { AnimatedStat } from './AnimatedStats';

export const Shipments = () => {
  const [selectedImg, setSelectedImg] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [scrollPos, setScrollPos] = useState(0);
  const scrollRef = React.useRef(null);
  
  const photos = [
    { src: '/blog/warehouse.jpg', caption: 'Склад у Китаї — готові до відправки' },
    { src: '/blog/loading.jpg', caption: 'Завантаження контейнера — акуратна укладка' },
    { src: '/blog/worker.jpg', caption: 'Наш працівник укладає коробки' },
    { src: '/blog/truck1.jpg', caption: 'Контейнер готовий до відправки' },
    { src: '/blog/truck2.jpg', caption: 'Щоденні відвантаження з Китаю' },
    { src: '/blog/tires.jpg', caption: 'Шини — завантажений контейнер' },
    { src: '/blog/boxes_pallet.jpg', caption: 'Палети з коробками на складі' },
    { src: '/blog/boxes_stack.jpg', caption: 'Стопки коробок готові до відправки' },
    { src: '/blog/container_inside.jpg', caption: 'Всередині контейнера — акуратне складування' },
    { src: '/blog/container_full.jpg', caption: 'Повністю завантажений контейнер' },
    { src: '/blog/forklift_worker.jpg', caption: 'Завантаження палет у контейнер' },
    { src: '/blog/container_loading.jpg', caption: 'Укладаємо коробки до стелі' },
    { src: '/blog/night_shipping.jpg', caption: 'Нічне відвантаження — працюємо цілодобово' },
  ];

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 320;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) setScrollPos(el.scrollLeft);
  };

  const canScrollLeft = scrollPos > 0;
  const canScrollRight = scrollRef.current ? scrollPos < scrollRef.current.scrollWidth - scrollRef.current.clientWidth - 10 : true;
  
  return (
    <section className="py-10 md:py-14 bg-[#02040a] relative overflow-hidden" id="shipments" data-testid="shipments-section">
      <div className="relative mx-3 sm:mx-6 lg:mx-10 rounded-3xl border border-yellow-400/60 shadow-[0_0_45px_-8px_rgba(250,204,21,0.45)] bg-[#050508] overflow-hidden py-14 md:py-20">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.16) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-yellow-500/5 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.07) 0%, transparent 65%)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Наші відвантаження</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Ми працюємо на якість кожної відвантаження та доставки. Щодня наша команда ретельно 
            готує, пакує та відправляє вантажі з Китаю в Україну.
          </p>
        </div>
        
        {/* Photo Carousel */}
        <div className="relative mb-12">
          {/* Left Arrow */}
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-[#1e293b] border border-slate-600 flex items-center justify-center transition-all duration-300 ${
              canScrollLeft ? 'opacity-100 hover:bg-yellow-500 hover:border-yellow-400 hover:text-black hover:scale-110' : 'opacity-30 cursor-default'
            }`}
            style={{ left: '-6px' }}
            disabled={!canScrollLeft}
            data-testid="carousel-left"
          >
            <svg className="w-6 h-6 carousel-arrow-left" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {/* Right Arrow */}
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-[#1e293b] border border-slate-600 flex items-center justify-center transition-all duration-300 ${
              canScrollRight ? 'opacity-100 hover:bg-yellow-500 hover:border-yellow-400 hover:text-black hover:scale-110' : 'opacity-30 cursor-default'
            }`}
            style={{ right: '-6px' }}
            disabled={!canScrollRight}
            data-testid="carousel-right"
          >
            <svg className="w-6 h-6 carousel-arrow-right" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          {/* Scrollable container */}
          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-4 overflow-x-auto scroll-smooth px-2 pb-4 shipments-carousel"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            data-testid="carousel-container"
          >
            {photos.map((photo, idx) => (
              <div 
                key={idx} 
                className="group relative flex-shrink-0 w-72 rounded-xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.03]"
                onClick={() => setSelectedImg(photo)}
                data-testid={`shipment-photo-${idx}`}
              >
                <div className="aspect-[4/3]">
                  <img 
                    src={photo.src} 
                    alt={photo.caption}
                    className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="absolute bottom-3 left-3 right-3 text-white text-sm font-medium">{photo.caption}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Scroll indicator dots */}
          <div className="flex justify-center mt-4 gap-1.5">
            {photos.map((_, idx) => (
              <div 
                key={idx} 
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  Math.floor(scrollPos / 300) === idx ? 'bg-yellow-400 w-6 shadow-[0_0_10px_rgba(250,204,21,0.7)]' : 'bg-slate-600'
                }`}
              />
            ))}
          </div>

          {/* Gallery button */}
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setGalleryOpen(true)}
              className="group inline-flex items-center gap-2 h-13 px-8 py-3.5 rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-[#1a0a00] font-extrabold text-sm md:text-base uppercase tracking-wide shadow-[0_0_25px_rgba(251,146,60,0.55)] hover:shadow-[0_0_45px_rgba(251,146,60,0.9)] hover:scale-[1.03] transition-all"
              data-testid="open-gallery-btn"
            >
              <Images className="w-5 h-5" />
              Галерея
            </button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { num: '50000+', label: 'Успішних доставок' },
            { num: '10+', label: 'Років досвіду' },
            { num: '24/7', label: 'Підтримка клієнтів' },
            { num: '99%', label: 'Вчасна доставка' },
          ].map((stat, idx) => (
            <AnimatedStat key={idx} num={stat.num} label={stat.label} electro />
          ))}
        </div>
      </div>
      </div>

      {/* Gallery modal */}
      {galleryOpen && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-3 md:p-6 animate-fadeIn" onClick={() => setGalleryOpen(false)} data-testid="gallery-modal">
          <div
            className="relative w-full max-w-5xl max-h-[88vh] rounded-3xl border border-yellow-400/60 bg-[#050508] shadow-[0_0_60px_-8px_rgba(250,204,21,0.55)] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.12) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
            <div className="relative flex items-center justify-between px-5 py-4 border-b border-yellow-400/25">
              <h3 className="text-white font-black text-lg md:text-xl uppercase tracking-wide flex items-center gap-2">
                <Images className="w-5 h-5 text-yellow-400" />
                Наші відвантаження — <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">галерея</span>
              </h3>
              <button onClick={() => setGalleryOpen(false)} className="w-9 h-9 rounded-full bg-slate-800/80 border border-slate-600 text-slate-300 hover:text-yellow-400 hover:border-yellow-400 transition-colors flex items-center justify-center" data-testid="gallery-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative overflow-y-auto p-4 md:p-6 electro-scroll" style={{ scrollBehavior: 'smooth' }} data-testid="gallery-grid">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {photos.map((photo, idx) => (
                  <div
                    key={idx}
                    className="group relative rounded-2xl overflow-hidden cursor-pointer border border-slate-700/60 hover:border-yellow-400/70 hover:shadow-[0_0_22px_-4px_rgba(250,204,21,0.55)] transition-all duration-300 hover:scale-[1.02]"
                    onClick={() => setSelectedImg(photo)}
                    data-testid={`gallery-photo-${idx}`}
                  >
                    <div className="aspect-[4/3]">
                      <img src={photo.src} alt={photo.caption} className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300" loading="lazy" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="absolute bottom-2.5 left-3 right-3 text-white text-xs md:text-sm font-medium">{photo.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Lightbox */}
      {selectedImg && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImg(null)}
          data-testid="shipment-lightbox"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImg.src} 
              alt={selectedImg.caption} 
              className="w-full rounded-xl shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-lg">{selectedImg.caption}</p>
            <button 
              onClick={() => setSelectedImg(null)}
              className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-800 shadow-lg hover:bg-slate-100 transition-colors"
              data-testid="close-lightbox"
            >
              X
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
